import type {ContainerStatusEntity, TransportCategory} from "@ots-frontend/ots-data";
import {type ContainerStatusDisplayOptions, DEFAULT_CONTAINER_STATUS_DISPLAY, isPollStale} from "../container-status/container-status-display";

export type SiteContainerTone = "critical" | "warning" | "normal";

/** One container at a site, as the map's site panel shows it. */
export type SiteContainerRow = {
  id: string;
  name: string;
  /** How full, 0–100. */
  percent?: number;
  /** The oil share of `percent`, 0–100; the rest is water. */
  oilPercent?: number;
  /** The part of the container that can be hauled now, 0–100: the top of the fill, above the minimum level. */
  readyPercent?: number;
  tone: SiteContainerTone;
  availableLoads: number;
  availableVolume?: number;
  volume?: number;
  capacity?: number;
  hoursToFull?: number;
  hoursToFullCritical: boolean;
  polledAt?: number;
  stalePoll: boolean;
  doNotUse: boolean;
  doNotDispatch: boolean;
  hauler?: string;
};

export type SiteContainers = {
  containers: SiteContainerRow[];
  availableLoads: number;
  /** Assigned haulers across the site's containers, most containers first. */
  haulers: string[];
  /** The soonest any container fills. */
  hoursToFull?: number;
  hoursToFullCritical: boolean;
  /** The site's transport flags as the container statuses carry them, which the container status grid draws. */
  waterViaPipeline: boolean;
  oilViaPipeline: boolean;
};

export type SiteContainerContext = {
  now: number;
  category?: TransportCategory;
  /** The browser's Container Status display options, so "stale poll" means the same here as on that page. */
  display?: ContainerStatusDisplayOptions;
};

const percent = (fraction: number | undefined) => fraction == null ? undefined : Math.max(0, Math.min(100, fraction * 100));

const toneOf = (status: ContainerStatusEntity): SiteContainerTone => {
  const fill = status.meterFill;
  if (fill?.actualStatus === "CRITICAL" || fill?.actualTimeToFullCritical) return "critical";
  if (fill?.actualStatus === "WARNING" || fill?.exceedsMaxNonPrimaryVolume) return "warning";
  return "normal";
};

export const toSiteContainerRow = (status: ContainerStatusEntity, context: SiteContainerContext): SiteContainerRow => {
  const fill = status.meterFill;
  const capacity = status.containerVolume;
  const fillPercent = percent(fill?.actualPercent);
  const readyPercent = capacity && fill?.availableLoadVolume != null
    ? Math.min(fillPercent ?? 0, (fill.availableLoadVolume / capacity) * 100)
    : undefined;
  return {
    id: status.containerId,
    name: status.containerName,
    percent: fillPercent,
    oilPercent: percent(status.oilMeterFill?.actualPercent),
    readyPercent,
    tone: toneOf(status),
    availableLoads: fill?.availableLoadCount ?? 0,
    availableVolume: fill?.availableLoadVolume,
    volume: fill?.actualVolume,
    capacity,
    hoursToFull: fill?.actualTimeToFull,
    hoursToFullCritical: fill?.actualTimeToFullCritical === true,
    polledAt: fill?.dttm,
    stalePoll: isPollStale(status, context.display ?? DEFAULT_CONTAINER_STATUS_DISPLAY, context),
    doNotUse: status.containerDoNotUse === true,
    doNotDispatch: status.containerDoNotDispatch === true,
    hauler: status.assignedCompanyName || undefined,
  };
};

/** A site's containers rolled up: loads ready, who hauls them, and how soon one fills. */
export const summarizeSiteContainers = (statuses: ContainerStatusEntity[], context: SiteContainerContext): SiteContainers => {
  const containers = statuses.map(status => toSiteContainerRow(status, context));
  const haulerCounts = new Map<string, number>();
  for (const {hauler} of containers) if (hauler) haulerCounts.set(hauler, (haulerCounts.get(hauler) ?? 0) + 1);
  const soonest = containers
    .filter(row => row.hoursToFull != null)
    .sort((a, b) => a.hoursToFull! - b.hoursToFull!)[0];
  return {
    containers,
    availableLoads: containers.reduce((sum, row) => sum + row.availableLoads, 0),
    haulers: [...haulerCounts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name]) => name),
    hoursToFull: soonest?.hoursToFull,
    hoursToFullCritical: soonest?.hoursToFullCritical ?? false,
    waterViaPipeline: statuses.some(status => status.siteTransportViaPipeline === true),
    oilViaPipeline: statuses.some(status => status.siteTransportOilViaPipeline === true),
  };
};

export type SiteLoadBadge = {
  availableLoads: number;
  /** Any container at the site is critical or about to fill. */
  critical: boolean;
};

/** Available loads per site id, for the badges on the site markers. Sites with nothing ready are left out. */
export const siteLoadBadges = (statuses: ContainerStatusEntity[]): Map<string, SiteLoadBadge> => {
  const badges = new Map<string, SiteLoadBadge>();
  for (const status of statuses) {
    if (!status.siteId) continue;
    const badge = badges.get(status.siteId) ?? {availableLoads: 0, critical: false};
    badge.availableLoads += status.meterFill?.availableLoadCount ?? 0;
    badge.critical ||= toneOf(status) === "critical";
    badges.set(status.siteId, badge);
  }
  for (const [siteId, badge] of badges) if (badge.availableLoads <= 0) badges.delete(siteId);
  return badges;
};

/** "just now", "12 min ago", "3 h ago", "2 d ago". */
export const formatAgo = (at: number | undefined, now: number) => {
  if (at == null) return undefined;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `${hours} h ago` : `${Math.floor(hours / 24)} d ago`;
};

/** Time to full, which the server gives in hours: "< 1 h", "6.5 h", "3 d". */
export const formatHoursToFull = (hours: number | undefined) => {
  if (hours == null) return undefined;
  if (hours < 1) return "< 1 h";
  if (hours < 48) return `${Math.round(hours * 10) / 10} h`;
  return `${Math.round(hours / 24)} d`;
};
